/**
 * Sentry custom-span helpers.
 *
 * Spans created here attach to the active pageload/navigation transaction and
 * inherit its sampling decision (no extra config). Use a dot-namespaced `op`
 * so spans group cleanly in Explore > Traces (e.g. `app.init.*`, `page.init.*`).
 */
import * as Sentry from '@sentry/sveltekit';

/**
 * Run `fn` inside a Sentry span and return its result.
 * The span ends when `fn` settles. Synchronous and async `fn` are both supported.
 * @template T
 * @param {string} op - Dot-namespaced operation, e.g. `app.init.experiments`.
 * @param {string} name - Human-readable span name.
 * @param {(span: import('@sentry/core').Span | undefined) => T} fn - Work to measure; receives the span so it can set attributes.
 * @param {Record<string, string | number | boolean>} [attributes] - Attributes known up front.
 * @returns {T}
 */
export function instrument(op, name, fn, attributes) {
	// Defensive: if tracing is unavailable (e.g. partially-mocked Sentry in
	// tests), run the work directly so instrumentation never alters behavior.
	if (typeof Sentry.startSpan !== 'function') {
		return fn(undefined);
	}
	return Sentry.startSpan({ name, op, attributes }, fn);
}

/**
 * Record that `page.init()` resolved the variant component. Emits an
 * `abc.variant_ready` User Timing mark and a Sentry `time_to_variant_ready`
 * measurement (ms since timeOrigin). This is a **JS-resolution** timestamp:
 * it fires synchronously after the component class is available, before Svelte
 * commits the variant DOM and before the variant is painted. Do not interpret
 * it as "usable page painted" — use `markVariantPainted` for that.
 */
export function markVariantReady() {
	sentryMeasurement('time_to_variant_ready');
	markMilestone('variant_ready');
	setRouteAttributes({ 'abc.variant_ready': true });
}

/**
 * Record that the prerendered skeleton became visible. Emits an
 * `abc.skeleton_visible` User Timing mark and a Sentry `time_to_skeleton_visible`
 * measurement. Schedule this via `afterPaint` from the skeleton's `onMount` so
 * the mark lands after a paint boundary rather than at assignment time.
 */
export function markSkeletonVisible() {
	sentryMeasurement('time_to_skeleton_visible');
	markMilestone('skeleton_visible');
	setRouteAttributes({ 'abc.skeleton_visible': true });
}

/**
 * Record that the resolved variant has been painted. Emits an `abc.variant_painted`
 * User Timing mark and a Sentry `time_to_variant_painted` measurement. Call this
 * after `markVariantReady`, a Svelte `tick()` (DOM commit), and an `afterPaint`
 * paint boundary so the timestamp reflects a painted variant, not just resolution.
 */
export function markVariantPainted() {
	sentryMeasurement('time_to_variant_painted');
	markMilestone('variant_painted');
	measureBetween('variant_paint_lag', 'variant_ready', 'variant_painted');
	emitSkeletonLead();
	setRouteAttributes({ 'abc.variant_painted': true });
}

/**
 * Record that the shared subscription payment form became interactive (default
 * credit-card form ready for input). Emits a route-relative
 * `route_time_to_payment_form_ready` measurement on the active transaction. One-shot
 * per route; duplicate calls are suppressed by `markMilestone`.
 * @returns {void}
 */
export function markPaymentFormReady() {
	markMilestone('payment_form_ready');
}

/**
 * Run `fn` after the next two animation frames so it executes after the browser
 * has had a chance to paint the current DOM. Returns a cancel function so callers
 * (e.g. a skeleton that unmounts before painting) can abort a pending callback.
 * In non-browser/test environments without `requestAnimationFrame`, runs `fn` on
 * the next microtask so instrumentation stays async-safe without breaking tests.
 * @param {() => void} fn - Work to run after a paint boundary.
 * @returns {() => void}
 */
export function afterPaint(fn) {
	if (typeof requestAnimationFrame !== 'function') {
		let cancelled = false;
		Promise.resolve().then(() => {
			if (!cancelled) fn();
		});
		return () => {
			cancelled = true;
		};
	}
	let raf1 = 0;
	let raf2 = 0;
	let cancelled = false;
	raf1 = requestAnimationFrame(() => {
		raf2 = requestAnimationFrame(() => {
			if (!cancelled) fn();
		});
	});
	return () => {
		cancelled = true;
		if (raf1) cancelAnimationFrame(raf1);
		if (raf2) cancelAnimationFrame(raf2);
	};
}

/** @param {string} name - User Timing mark name. */
function userTimingMark(name) {
	if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
		try {
			performance.mark(name);
		} catch {
			/* ignore — mark API must not break rendering */
		}
	}
}

/** @param {string} name - Sentry measurement name. */
function sentryMeasurement(name) {
	if (typeof Sentry.setMeasurement === 'function') {
		try {
			const ms =
				typeof performance !== 'undefined' && typeof performance.now === 'function'
					? performance.now()
					: 0;
			Sentry.setMeasurement(name, ms, 'millisecond');
		} catch {
			/* ignore — measurement must not break rendering */
		}
	}
}

/**
 * Emit a Sentry measurement with an explicit value (ms). Used for route-relative
 * durations and derived per-visit durations (e.g. skeleton_lead). No-ops when
 * tracing is unavailable; never throws.
 * @param {string} name - Measurement name.
 * @param {number} ms - Duration in milliseconds.
 * @returns {void}
 */
function sentryMeasurementValue(name, ms) {
	if (typeof Sentry.setMeasurement !== 'function') return;
	if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return;
	try {
		Sentry.setMeasurement(name, ms, 'millisecond');
	} catch {
		/* ignore — measurement must not break rendering */
	}
}

/* -------------------------------------------------------------------------- *
 * Route-relative journey telemetry
 *
 * The existing mark helpers above emit absolute `time_to_*` measurements
 * (ms since document timeOrigin). Those cannot be subtracted at the aggregate
 * level to compute per-visit durations like skeleton_lead. The helpers below
 * record a per-route start timestamp and emit route-relative measurements and
 * direct derived durations on the active transaction, so percentile math is
 * statistically valid. Existing marks are retained during migration.
 * -------------------------------------------------------------------------- */

/**
 * @type {number} performance.now() captured at the most recent route start.
 *
 * Initialized to 0 — document `timeOrigin` — so the initial hard pageload has a
 * valid baseline from module load. Svelte mounts children before parents, so the
 * page's `onMount` marks fire before the root layout's; anything that waited for
 * the layout to establish a baseline would miss them.
 */
let routeStartMs = 0;

/** @type {Map<string, number>} milestone name -> performance.now() at mark time. */
const milestones = new Map();

/**
 * Mark the start of a route. Resets per-route milestone state and establishes
 * the baseline for route-relative `route_time_to_*` measurements.
 *
 * Call this from `+layout.svelte` `beforeNavigate` only. SPA navigations are the
 * only case that needs it: hard pageloads use the module-load baseline of 0
 * (`timeOrigin`). Calling it from `onMount` would clear milestones the page's own
 * `onMount` had already recorded, since Svelte mounts children before parents.
 *
 * No-ops when `performance.now` is unavailable.
 * @returns {void}
 */
export function recordRouteStart() {
	const now = nowMs();
	if (now == null) return;
	routeStartMs = now;
	milestones.clear();
}

/**
 * Record a one-time milestone relative to the current route start. Emits a
 * User Timing mark (`abc.<name>`), a route-relative Sentry measurement
 * (`time_to_<name>`), and stores the timestamp for later derived durations.
 * Suppresses duplicate milestones and calls made before `recordRouteStart`.
 * @param {string} name - Milestone name (without `abc.` prefix).
 * @returns {void}
 */
export function markMilestone(name) {
	if (!name || milestones.has(name)) return;
	const now = nowMs();
	if (now == null) return;
	milestones.set(name, now);
	userTimingMark(`abc.${name}`);
	sentryMeasurementValue(`route_time_to_${name}`, now - routeStartMs);
}

/**
 * Emit `skeleton_lead`: how long the prerendered skeleton was on screen before
 * the variant painted. Emitted for every visit that painted a variant, clamped
 * at zero, including visits where the skeleton never painted. Dropping those
 * would bias the aggregate upward by counting only the visits the shell helped.
 * @returns {void}
 */
function emitSkeletonLead() {
	const painted = milestones.get('variant_painted');
	if (painted == null) return;
	const skeletonVisible = milestones.get('skeleton_visible');
	const lead = skeletonVisible == null ? 0 : Math.max(0, painted - skeletonVisible);
	sentryMeasurementValue('skeleton_lead', lead);
}

/**
 * `performance.now()` when the API is available, otherwise null.
 * @returns {number | null}
 */
function nowMs() {
	if (typeof performance === 'undefined' || typeof performance.now !== 'function') return null;
	return performance.now();
}

/**
 * Emit a derived duration between two previously-recorded milestones on the
 * active transaction. Suppresses when either milestone is missing or the end
 * precedes the start (misordered). Never emits a negative or zero-from-missing
 * value.
 * @param {string} name - Measurement name (e.g. `skeleton_lead`).
 * @param {string} fromMilestone - Start milestone name.
 * @param {string} toMilestone - End milestone name.
 * @returns {void}
 */
export function measureBetween(name, fromMilestone, toMilestone) {
	if (!name) return;
	const from = milestones.get(fromMilestone);
	const to = milestones.get(toMilestone);
	if (from == null || to == null) return;
	sentryMeasurementValue(name, to - from);
}

/**
 * Attach bounded attributes to the active transaction. Silently drops null /
 * undefined / non-primitive values and never throws. Best-effort: when no
 * active span is reachable (tracing disabled, partially-mocked Sentry), the
 * call is a no-op.
 * @param {Record<string, string | number | boolean | null | undefined>} attributes - Bounded primitive attributes to attach.
 * @returns {void}
 */
export function setRouteAttributes(attributes) {
	if (!attributes || typeof attributes !== 'object') return;
	const span = getTransactionSpan();
	if (!span || typeof span.setAttributes !== 'function') return;
	try {
		/** @type {Record<string, string | number | boolean>} */
		const cleaned = {};
		for (const [key, value] of Object.entries(attributes)) {
			if (value == null) continue;
			if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
				cleaned[key] = value;
			}
		}
		if (Object.keys(cleaned).length > 0) span.setAttributes(cleaned);
	} catch {
		/* attributes are best-effort */
	}
}

/**
 * Returns the root span (transaction) for the currently-active span. Attributes
 * must land on the transaction: the SDK only serializes the root span's
 * attributes into `contexts.trace.data`, so anything attached to a child span
 * (e.g. the `function.sveltekit.load` span wrapping every `load`) is invisible
 * to transaction-level queries. `Sentry.setMeasurement` already resolves the
 * root internally; this keeps attributes consistent with it.
 * @returns {import('@sentry/core').Span | undefined}
 */
function getTransactionSpan() {
	const active = getActiveSpan();
	if (!active) return undefined;
	if (typeof Sentry.getRootSpan !== 'function') return active;
	try {
		return Sentry.getRootSpan(active) ?? active;
	} catch {
		return active;
	}
}

/**
 * Returns the currently-active Sentry span/transaction, or null when tracing
 * is unavailable. Tries the v8 `getActiveSpan` API and falls back to the
 * hub/scope span lookup for older SDK surfaces.
 * @returns {import('@sentry/core').Span | undefined}
 */
function getActiveSpan() {
	try {
		if (typeof Sentry.getActiveSpan === 'function') {
			const span = Sentry.getActiveSpan();
			if (span) return span;
		}
		const hub = typeof Sentry.getCurrentHub === 'function' ? Sentry.getCurrentHub() : null;
		const scope = hub && typeof hub.getScope === 'function' ? hub.getScope() : null;
		// `getSpan` was removed from the v8 Scope type; cast to any for the
		// defensive lookup so this keeps working across SDK surfaces without
		// a compile error.
		const span =
			scope && typeof (/** @type {any} */ (scope).getSpan) === 'function'
				? /** @type {any} */ (scope).getSpan()
				: null;
		return span ?? undefined;
	} catch {
		return undefined;
	}
}
